import { SerializedData, Store } from "bernie-core";
import { action, computed, observable } from "mobx";
import { Hotel } from "typings/microserviceModels/hotels-flex-module";
import { DxHotel, StoredDxHotel } from "components/flexComponents/DestinationWhereToStay/typings.d.ts";
import { Logger, NOOP_LOGGER } from "bernie-logger";
import { BexApiService } from "src/services/BexApiService";
import { getBexApiContext } from "bernie-context";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import {
  CustomerPropertySearchByIdQuery,
  CustomerSearchHistoryQuery,
  DateInput,
  DxPropertySearchQuery,
  HistoryType,
  MapPropertySearchById,
  MapPropertySearchByLatLong,
  OperationResult,
  PropertyDateRangeInput,
} from "__generated__/api";
import { getNextFriday } from "src/components/utility/DateUtils";
import { BexApiMessage } from "model/BexApiMessage";
import { MapPlace } from "uitk-react-experimental-module-map";
import { formatText } from "bernie-l10n";
import { FAILED_HOTEL_FETCH } from "src/config/systemEvents";
import { NewPriceItems, RecentlyViewedHotel } from "src/components/flexComponents/RecentlyViewedHotels/typings";
import Messages = MapPropertySearchByLatLong.Messages;
import Query = MapPropertySearchByLatLong.Query;
import Items = CustomerSearchHistoryQuery.Items;
import Properties = CustomerPropertySearchByIdQuery.Properties;
import StartDate = CustomerSearchHistoryQuery.StartDate;
import EndDate = CustomerSearchHistoryQuery.EndDate;

export enum VariationType {
  PRICE_INCREASE = "increase",
  PRICE_DECREASE = "decrease",
  AVAILABLE = "available",
  SOLD_OUT = "sold_out",
}
export class HotelsStore extends Store {
  @observable
  public allHotelsOnMap: MapPlace[] = [];

  @observable
  public storedWhereToStayHotels: StoredDxHotel = [];

  @observable
  public bexApiMessages: BexApiMessage[] = [];

  @observable
  public isLoading: boolean = false;

  public constructor(state: SerializedData = {}, logger: Logger = NOOP_LOGGER, private bexApiService: BexApiService) {
    super(state, logger);
    this.bexApiService = bexApiService;
  }

  public setStoredWhereToStayHotels = (neighborhoodId: string, hotels: DxHotel[]) => {
    this.storedWhereToStayHotels = {
      ...this.storedWhereToStayHotels,
      [neighborhoodId]: hotels,
    };
  };

  /**
   * Set recently viewed hotels and hotels
   * Add infositeURL and newSearchPrice to recentViewedHotel object
   * newSearchPrice is the hotel price and originalSearchPrice is the recently view hotel price
   * @param {RecentlyViewedHotel[]} recentlyViewedHotels - Array of recently hotels viewed by the user
   * @param {CustomerPropertySearchByIdQuery.Properties[]} freshHotelData - Array of hotels that match with the recently view hotel
   * @memberof HotelsStore
   */
  private getFreshRecentlyViewedHotels = (
    recentlyViewedHotels: Items[],
    freshHotelData: OperationResult<CustomerPropertySearchByIdQuery.Query>[]
  ): RecentlyViewedHotel[] => {
    // facilitate our lives by building map of fresh hotel data with id-startDate-endDate key
    const buildDateKey = (date: StartDate | EndDate | DateInput) => `${date.year}-${date.month}-${date.day}`;
    const buildKey = (
      id: string,
      checkInDate: StartDate | EndDate | DateInput,
      checkOutDate: StartDate | EndDate | DateInput
    ) => `${id}-${buildDateKey(checkInDate)}-${buildDateKey(checkOutDate)}`;

    const mapOfFreshHotelData: { [key: string]: Properties } = freshHotelData
      .filter((result) => result?.data?.propertySearch)
      .map((result) => result.data)
      .reduce((acc, data) => {
        const { propertySearch } = data;
        const mapOfProperties = propertySearch.properties.reduce(
          (acc2, property) => ({
            ...acc2,
            [buildKey(
              property.id,
              propertySearch.searchCriteria.dateRange.checkInDate,
              propertySearch.searchCriteria.dateRange.checkOutDate
            )]: property,
          }),
          {}
        );

        return {
          ...acc,
          ...mapOfProperties,
        };
      }, {});

    const getFreshHotel = (recentlyViewedHotel: Items) =>
      mapOfFreshHotelData[
        buildKey(
          recentlyViewedHotel.productInfo?.propertyId || "",
          recentlyViewedHotel.startDate,
          recentlyViewedHotel.endDate
        )
      ];

    return recentlyViewedHotels.filter(getFreshHotel).map((recentlyViewedHotel) => {
      // get fresh hotel
      const freshHotel = getFreshHotel(recentlyViewedHotel);

      // check for price variation
      const firstPriceOption = freshHotel.price?.options && freshHotel.price?.options[0];
      const priceVariation = this.getPriceVariation(recentlyViewedHotel, firstPriceOption);

      return {
        ...recentlyViewedHotel,
        ...priceVariation,
        hisUrl: freshHotel.infositeURL,
      };
    });
  };

  /**
   * Add a new property to recently viewed hotel's object regarding price variation
   */
  private getPriceVariation = (
    hotelFromCustomerSearchHistory: CustomerSearchHistoryQuery.Items & NewPriceItems,
    updatedPrice?: CustomerPropertySearchByIdQuery.Options | null
  ): NewPriceItems => {
    const { originalSearchPrice } = hotelFromCustomerSearchHistory;
    const originalPrice = originalSearchPrice ? Math.round(originalSearchPrice.amount) : 0;
    const currentPrice = updatedPrice?.displayPrice ? Math.round(updatedPrice.displayPrice.amount) : 0;

    // if no new pricing then it is sold out
    if (!currentPrice) {
      return {
        priceChange: VariationType.SOLD_OUT,
      };
    }

    const newPriceItem: NewPriceItems = {};

    // Is price decreased
    if (originalPrice > currentPrice) {
      newPriceItem.priceChange = VariationType.PRICE_DECREASE;
    }

    // Is increased
    if (originalPrice < currentPrice) {
      newPriceItem.priceChange = VariationType.PRICE_INCREASE;
    }

    // Is now available
    if (!originalPrice && currentPrice) {
      newPriceItem.priceChange = VariationType.AVAILABLE;
    }

    // Set new price
    if (currentPrice) {
      newPriceItem.newPrice = currentPrice;
      newPriceItem.newPriceFormatted = updatedPrice?.displayPrice?.formatted;
    }

    return newPriceItem;
  };

  @action
  public async fetchHotels(hotelIds: string[], contextStore: ExtendedContextStore): Promise<any> {
    if (!hotelIds.length) {
      return [];
    }

    const variables: DxPropertySearchQuery.Variables = {
      context: getBexApiContext(contextStore),
      hotelIds,
    };

    try {
      let hotels: DxHotel[] = [];

      const query = await this.bexApiService.fetchDxPropertySearchQuery(variables);
      const hotelsFromResponse = query.data.propertySearch.properties;
      const hotelsLength = 4;

      /* istanbul ignore next line */
      for (const hotel of hotelsFromResponse) {
        if (hotels.length === hotelsLength) {
          break;
        }

        const { id, name, gallery, infositeURL, reviews, price } = hotel;
        if (!(id && name && gallery && infositeURL && reviews && price)) {
          continue;
        }

        const newHotel: DxHotel = {
          id,
          name,
          hotelSearchLink: infositeURL,
          price: price.lead ? price.lead.formatted : "",
          rating: reviews.score,
          reviews: reviews.total,
          hotelImage: gallery[0].url,
          priceValue: price.lead ? price.lead.amount : 0,
        };

        hotels = [...hotels, newHotel];
      }

      return hotels;
    } catch (err) {
      return [];
    }
  }

  @action
  public async fetchMapHotelsById(hotels: Hotel[], contextStore: ExtendedContextStore, locationId: string) {
    this.isLoading = true;

    const searchDateRange: PropertyDateRangeInput = this.getSearchDateRange();
    const checkInDate = `${searchDateRange.checkInDate.year}-${searchDateRange.checkInDate.month}-${searchDateRange.checkInDate.day}`;

    const checkOutDate = `${searchDateRange.checkOutDate.year}-${searchDateRange.checkOutDate.month}-${searchDateRange.checkOutDate.day}`;

    const mapCompositionHotelsToMap = () => {
      this.allHotelsOnMap.push(...hotels.map((h) => this.mapFlexHotelToPropertyForMap(h, contextStore, locationId)));
    };

    const variables: MapPropertySearchById.Variables = {
      context: getBexApiContext(contextStore),
      propertyIds: hotels.map((hotel) => hotel.hotelId),
      dateRange: searchDateRange,
    };

    try {
      const query = await this.bexApiService.fetchMapHotelsById(variables);
      const newHotels: MapPlace[] = [];

      for (const property of query.data.propertySearch.properties) {
        // Check if the property has all the necessary info to be displayed on the map
        if (!property.mapMarker || !property.mapMarker.latLong || !property.infositeURL) {
          continue;
        }

        const formattedPrice = property.price && property.price.lead && property.price.lead.formatted;

        const priceAmount = property.price && property.price.lead && property.price.lead.amount;

        const newHotel: MapPlace = {
          id: property.id,
          placeName: property.name,
          formattedPrice: priceAmount ? formattedPrice : "",
          latitude: property.mapMarker.latLong.latitude,
          longitude: property.mapMarker.latLong.longitude,
          overallReviewRating: formatText("maps.sheets.hotel.reviewRating.text", property.reviews.score),
          reviewCount: `(${formatText("maps.sheets.hotel.reviews.text", property.reviews.total)})`,
          localizedSubtitle: property.reviews.localizedSubtitle,
          imageUrl: property.image && property.image.url,
          hrefUrl: this.constructPinnedHSRUrl(locationId, property.id, checkInDate, checkOutDate),
          icon: {
            type: priceAmount ? "price" : "soldOut",
          },
        };

        newHotels.push(newHotel);
      }

      this.allHotelsOnMap = newHotels;
    } catch (err) {
      mapCompositionHotelsToMap();
    } finally {
      this.isLoading = false;
    }
  }

  public async fetchUpdatedRecentlyViewedHotels(recentlyViewedHotels: Items[], contextStore: ExtendedContextStore) {
    // group by start date - end date
    const buildDateKey = (hotel: Items) => `${hotel.startDate?.raw}-${hotel.endDate?.raw}`;
    const hotelsGroupedByDates: {
      [key: string]: RecentlyViewedHotel[];
    } = recentlyViewedHotels
      .filter((item) => item !== null)
      .reduce(
        (acc, hotel) => ({
          ...acc,
          [buildDateKey(hotel)]: (acc[buildDateKey(hotel)] || []).concat(hotel),
        }),
        {}
      );

    // build variables for bex api
    const variables = Object.keys(hotelsGroupedByDates)
      .filter((dates) => hotelsGroupedByDates[dates].length > 0)
      .map((dates) => {
        const hotels = hotelsGroupedByDates[dates];
        const hotelIds = hotels
          .map((hotel) => hotel.productInfo?.propertyId)
          .filter((id) => typeof id === "string") as string[];
        const { startDate, endDate } = hotels[0];

        return {
          context: getBexApiContext(contextStore),
          hotelIds,
          dateRange: this.formatDate(startDate, endDate),
        };
      });

    try {
      // prepare promises and call bex api to get fresh data in parallel
      return await Promise.all(
        variables.map((variable) => this.bexApiService.fetchCustomerPropertySearchByIdQuery(variable))
      );
    } catch (err) {
      this.logger.logEvent(FAILED_HOTEL_FETCH, "Unexpected error when fetching customer property search by id", err);
      throw err;
    }
  }

  @action
  public async fetchRecentlyViewedHotels(contextStore: ExtendedContextStore, numberOfResults: number) {
    const variables: CustomerSearchHistoryQuery.Variables = {
      context: getBexApiContext(contextStore),
      lobs: [HistoryType.PROPERTY_DETAIL],
      numberOfResults,
    };

    try {
      // get recently viewed hotels
      const query = await this.bexApiService.fetchCustomerSearchHistoryQuery(variables);

      if (query.errors) {
        return [];
      }

      const recentlyViewedHotels = query.data?.customer?.searchHistory
        ?.reduce((acc, searchHistoryItemsArray) => [...acc, ...searchHistoryItemsArray.items], [])
        .filter((item) => item) as Items[];

      if (!recentlyViewedHotels?.length) {
        return [];
      }

      // get same hotels with fresh data
      const freshHotelData = await this.fetchUpdatedRecentlyViewedHotels(recentlyViewedHotels, contextStore);

      if (!freshHotelData?.length) {
        return [];
      }

      // merge together and return
      return this.getFreshRecentlyViewedHotels(
        recentlyViewedHotels,
        freshHotelData.filter((data) => !data.errors)
      );
    } catch (err) {
      this.logger.logEvent(FAILED_HOTEL_FETCH, "Unexpected error when fetching recently viewed hotels", err);
      throw err;
    }
  }

  /**
   * Fetch an array of hotels for a given latitude and longitude. Queries the
   * BEX API and reassigns the store's allHotelsOnMaps and isLoading property
   * and returns it.
   * @param {ExtendedContextStore} contextStore - Context to be passed to getBextApiContext
   * @param {number} lat - Latitude on the map that the user is looking at
   * @param {number} lng - Longitude on the map that the user is looking at
   * @returns {Promise<PropertyForMap[]>} - Array of found hotels. Defaults to empty array in case of errors
   * @memberof HotelsStore
   */
  @action
  public async fetchHotelsByLatLong(
    contextStore: ExtendedContextStore,
    lat: number,
    lng: number,
    locationId: string
  ): Promise<void> {
    // Used for loading indicators
    this.isLoading = true;
    // Get search date range to determine pricing of hotels near the user
    const searchDateRange: PropertyDateRangeInput = this.getSearchDateRange();
    // Format check in and check out date to be what PropertyForMap expects
    const checkInDate = `${searchDateRange.checkInDate.year}-${searchDateRange.checkInDate.month}-${searchDateRange.checkInDate.day}`;
    const checkOutDate = `${searchDateRange.checkOutDate.year}-${searchDateRange.checkOutDate.month}-${searchDateRange.checkOutDate.day}`;
    // Get BexAPI context, filters and prep rest of variables
    const variables: MapPropertySearchByLatLong.Variables = {
      context: getBexApiContext(contextStore),
      latitude: lat,
      longitude: lng,
      dateRange: searchDateRange,
    };

    try {
      const newHotels: MapPlace[] = [];
      const query = await this.bexApiService.fetchMapHotelsForLatLong(variables);
      this.populateBexApiMessages(query.data);
      // Check if the hotel/property has all the necessary info to be displayed on the map
      for (const property of query.data.propertySearch.properties) {
        if (property.mapMarker && property.mapMarker.latLong && property.infositeURL) {
          const newHotel: MapPlace = {
            id: property.id,
            placeName: property.name,
            formattedPrice:
              property.price && property.price.lead && property.price.lead.amount
                ? property.price.lead.formatted
                : undefined,
            latitude: property.mapMarker.latLong.latitude,
            longitude: property.mapMarker.latLong.longitude,
            overallReviewRating: formatText("maps.sheets.hotel.reviewRating.text", property.reviews.score),
            reviewCount: `(${formatText("maps.sheets.hotel.reviews.text", property.reviews.total)})`,
            localizedSubtitle: property.reviews.localizedSubtitle,
            imageUrl: property.image && property.image.url,
            hrefUrl: this.constructPinnedHSRUrl(locationId, property.id, checkInDate, checkOutDate),
            icon: {
              type: "price",
            },
          };

          newHotels.push(newHotel);
        }
      }

      // Replace existing properties
      this.allHotelsOnMap = newHotels;
    } catch (err) {
      // when the call fails, clear previous messages. Error component defaults to default message.
      this.bexApiMessages = [];
      throw err;
    } finally {
      // Done loading
      this.isLoading = false;
    }
  }

  /**
   * Getter for allHotelsOnMap. Filters through them to prevent the bug on BEX
   * API/our data where we sometimes have hotels on in the middle of the ocean
   * (0,0).
   * @readonly
   * @type {MapPlace[]}
   */
  @computed
  public get hotelsOnMap(): MapPlace[] {
    return this.allHotelsOnMap.filter((hotel: MapPlace) => hotel.latitude !== 0 || hotel.longitude !== 0);
  }

  /**
   * This method converts a hotel coming from the flex composition into a hotel that can be placed on the map.
   * @param hotel A hotel coming from the composition.
   * @return a hotel for map
   */
  public mapFlexHotelToPropertyForMap(hotel: Hotel, contextStore: ExtendedContextStore, locationId: string): MapPlace {
    const searchDateRange: PropertyDateRangeInput = this.getSearchDateRange();
    const checkInDate = `${searchDateRange.checkInDate.year}-${searchDateRange.checkInDate.month}-${searchDateRange.checkInDate.day}`;

    const checkOutDate = `${searchDateRange.checkOutDate.year}-${searchDateRange.checkOutDate.month}-${searchDateRange.checkOutDate.day}`;

    const language = contextStore.locale.split(/[_-]/);

    let overallReviewRating;
    if (hotel.hotelOverallReviewRating) {
      overallReviewRating = formatText(
        "maps.sheets.hotel.reviewRating.text",
        hotel.hotelOverallReviewRating.toLocaleString(language, { maximumFractionDigits: 1 })
      );
    }

    let reviewCount;
    if (hotel.reviewCount) {
      reviewCount = `(${formatText(
        "maps.sheets.hotel.reviews.text",
        hotel.reviewCount.toLocaleString(language, { maximumFractionDigits: 1 })
      )})`;
    }

    return {
      id: hotel.hotelId,
      placeName: hotel.hotelName,
      formattedPrice: hotel.formattedPrice,
      latitude: hotel.hotelLatitude,
      longitude: hotel.hotelLongitude,
      overallReviewRating,
      reviewCount,
      imageUrl: hotel.hotelImageUrl,
      hrefUrl: this.constructPinnedHSRUrl(locationId, hotel.hotelId, checkInDate, checkOutDate),
      icon: {
        type: hotel.formattedPrice ? "price" : "soldOut",
      },
    };
  }

  public hydrate(data: SerializedData): void {
    // The serialized store as returned by the server exposes a stripped down
    // version of bexApiService with no methods.We delete it here to prevent the override.
    delete data.bexApiService;

    Object.assign(this, data);
  }

  /**
   * Calculate and return search date range for hotels we want to find around
   * our user. We choose check-in on a Friday two weeks from now. Note that the
   * range is not based on what the user input in the Wizard, so it's not dynamic
   * @returns {PropertyDateRangeInput} - Check in date and check out dates
   */
  public getSearchDateRange(): PropertyDateRangeInput {
    //Check-in is Friday two weeks from now
    //Check out is the day after that
    const today = new Date();
    const checkInDate = getNextFriday(today);
    const checkOutDate = new Date(checkInDate);
    checkOutDate.setDate(checkInDate.getDate() + 1);

    return {
      checkInDate: {
        day: checkInDate.getDate(),
        month: checkInDate.getMonth() + 1,
        year: checkInDate.getFullYear(),
      },
      checkOutDate: {
        day: checkOutDate.getDate(),
        month: checkOutDate.getMonth() + 1,
        year: checkOutDate.getFullYear(),
      },
    };
  }

  public constructPinnedHSRUrl(regionId: string, hotelId: string, startDate: string, endDate: string) {
    return `/Hotel-Search?selected=${hotelId}&PinnedHotelID=${hotelId}&HadPinnedHotel=true&regionId=${regionId}&startDate=${startDate}&endDate=${endDate}`;
  }

  private populateBexApiMessages(query: Query) {
    this.bexApiMessages = query.propertySearch.summary.messages
      ? query.propertySearch.summary
          .messages!.filter((message) => validateBexApiMessage(message))
          .map((message) => {
            const subtitle: string | null =
              (message!.subtitle && message!.subtitle!.text && message!.subtitle!.text!) || null;

            return { type: message.type!, title: message.title!.text!, subtitle };
          })
      : [];
  }

  public formatDate(startDate: DateInput, endDate: DateInput): PropertyDateRangeInput {
    return {
      checkInDate: {
        day: startDate!.day,
        month: startDate!.month,
        year: startDate.year,
      },
      checkOutDate: {
        day: endDate.day,
        month: endDate.month,
        year: endDate.year,
      },
    };
  }
}

function validateBexApiMessage(message: Messages): boolean {
  return (
    message.title !== undefined &&
    message.title !== null &&
    message.title.text !== undefined &&
    message.title.text !== null &&
    message.type !== undefined &&
    message.type !== null &&
    message.subtitle !== undefined
  );
}
